(() => {
  const root = document.querySelector('[data-natto-quiz]');
  if (!root) return;

  const steps = [...root.querySelectorAll('.lnq-step')];
  const backButton = root.querySelector('[data-quiz-back]');
  const stepCount = root.querySelector('[data-quiz-count]');
  const progress = root.querySelector('[data-quiz-progress]');
  const stepHistory = [{ step: steps[0], answers: {} }];
  const historySession = `${Date.now()}-${Math.random()}`;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let answers = {};
  let currentStep = steps[0];
  let historyIndex = 0;
  let advanceTimer;

  const stepNames = [...new Set(steps.map((step) => step.dataset.step))];
  const keyOf = (step) => step.dataset.key || step.dataset.step;
  const optionLabel = (option) => option.dataset.label || option.textContent.trim();

  const getActiveSteps = () =>
    steps.filter((step) => !step.dataset.branch || step.dataset.branch === answers.concern?.value);

  // Labels come from the markup, so added or reordered steps and options need no GTM or PostHog edits.
  // GTM forwards the nested quiz object to PostHog only; read it with a version 1 Data Layer Variable.
  const track = (event, detail) => {
    const quiz = {
      quiz_id: 'natto',
      quiz_step: currentStep.dataset.step,
      quiz_step_type: currentStep.dataset.type,
      quiz_step_index: stepNames.indexOf(currentStep.dataset.step) + 1,
      quiz_step_total: stepNames.length,
      quiz_branch: answers.concern?.value,
    };
    // Only answers from steps before this one on the current path: branch variants share a key,
    // so this step's own slot may still hold another branch's answer.
    const activeSteps = getActiveSteps();
    activeSteps.slice(0, activeSteps.indexOf(currentStep)).forEach((active) => {
      const answer = answers[keyOf(active)];
      if (answer) quiz[`answer_${keyOf(active)}`] = answer.value;
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, quiz: { ...quiz, ...detail } });
  };

  const trackAnswer = (selected) => {
    const options = [...currentStep.querySelectorAll('.lnq-option, .lnq-card')];
    const labels = selected.map(optionLabel);
    const indexes = selected.map((option) => options.indexOf(option) + 1);
    const multi = currentStep.dataset.type === 'multi';
    track('quiz_answer', {
      quiz_answer_key: keyOf(currentStep),
      quiz_answer_value: answers[keyOf(currentStep)].value,
      quiz_answer_label: multi ? labels : labels[0],
      quiz_answer_index: multi ? indexes : indexes[0],
    });
  };

  const renderStep = (step) => {
    if (['single', 'multi'].includes(step.dataset.type)) {
      const selected = answers[keyOf(step)]?.selected || [];
      step.querySelectorAll('.lnq-option, .lnq-card').forEach((option) => {
        option.setAttribute('aria-pressed', selected.includes(option));
      });
      const nextButton = step.querySelector('[data-quiz-next]');
      if (nextButton) nextButton.disabled = selected.length === 0;
    }
    step.querySelectorAll('[data-branch-text]').forEach((el) => {
      el.hidden = el.dataset.branchText !== answers.concern?.value;
    });
    step.querySelectorAll('[data-natto-text]').forEach((el) => {
      el.hidden = !el.dataset.nattoText.split(' ').includes(answers.triedNatto?.value);
    });
    step.querySelectorAll('[data-answer]').forEach((el) => {
      el.textContent = answers[el.dataset.answer].summary;
    });

    const gauge = step.querySelector('[data-quiz-gauge]');
    if (gauge) {
      const level = Number(answers.duration.level);
      const labelIndex = Math.min(4, Math.floor(level / 20));
      const label = gauge.querySelectorAll('li')[labelIndex].textContent;
      gauge.style.setProperty('--level', `${level}%`);
      gauge.setAttribute('aria-label', `Your level: ${label}`);
    }
  };

  const renderProgress = (step) => {
    const activeSteps = getActiveSteps();
    const isCounted = (el) => !['loader', 'result'].includes(el.dataset.type);
    const countedIndex = activeSteps.filter(isCounted).indexOf(step);
    // Branch variants share a data-step name, so the total is right before a branch is chosen.
    const total = new Set(steps.filter(isCounted).map((el) => el.dataset.step)).size;
    stepCount.textContent = countedIndex === -1 ? '' : `${countedIndex + 1} of ${total}`;
    progress.style.inlineSize = `${Math.round((activeSteps.indexOf(step) / (activeSteps.length - 1)) * 100)}%`;
  };

  const showStep = (step, { remember = true, direction = remember ? 'forward' : 'back' } = {}) => {
    clearTimeout(advanceTimer);
    if (remember) {
      if (currentStep.dataset.type !== 'loader') {
        stepHistory[historyIndex] = { step: currentStep, answers: { ...answers } };
        // A new answer after Back replaces the abandoned forward path.
        stepHistory.length = historyIndex + 1;
        // Back can reach entries from a previous page load. Adopt the current
        // entry before starting a new path so its Back destination remains usable.
        if (window.history.state?.quizSession !== historySession) {
          window.history.replaceState(
            { ...window.history.state, quizSession: historySession, quizDepth: historyIndex },
            '',
          );
        }
        historyIndex += 1;
        window.history.pushState(
          { ...window.history.state, quizSession: historySession, quizDepth: historyIndex },
          '',
        );
      }
      // A loader and its following screen share a history entry.
      stepHistory[historyIndex] = { step, answers: { ...answers } };
    }
    currentStep.hidden = true;
    currentStep = step;
    renderStep(step);
    step.hidden = false;
    root.querySelectorAll('video').forEach((video) => {
      if (step.contains(video) && !reduceMotion.matches) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    const isLoader = step.dataset.type === 'loader';
    backButton.hidden = historyIndex === 0 || isLoader;
    renderProgress(step);
    step.querySelector('.lnq-title').focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
    track('quiz_step', { quiz_direction: direction });

    if (isLoader) advanceTimer = setTimeout(next, 1600);
  };

  const next = () => {
    const activeSteps = getActiveSteps();
    const nextStep = activeSteps[activeSteps.indexOf(currentStep) + 1];
    if (nextStep) showStep(nextStep);
  };

  root.addEventListener('click', (event) => {
    const option = event.target.closest('.lnq-option, .lnq-card');
    if (option && currentStep.contains(option)) {
      if (currentStep.dataset.type === 'multi') {
        option.setAttribute('aria-pressed', option.getAttribute('aria-pressed') !== 'true');
        const hasSelection = currentStep.querySelector('[aria-pressed="true"]');
        currentStep.querySelector('[data-quiz-next]').disabled = !hasSelection;
        return;
      }

      currentStep
        .querySelectorAll('[aria-pressed]')
        .forEach((el) => el.setAttribute('aria-pressed', 'false'));
      option.setAttribute('aria-pressed', 'true');
      const label = optionLabel(option);
      clearTimeout(advanceTimer);
      advanceTimer = setTimeout(() => {
        answers[keyOf(currentStep)] = {
          value: option.dataset.value || label,
          summary: option.dataset.summary || label,
          level: option.dataset.level,
          selected: [option],
        };
        trackAnswer([option]);
        next();
      }, 250);
      return;
    }

    const video = event.target.closest('.lnq-media')?.querySelector('video');
    if (video && currentStep.contains(video)) {
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
    const nextButton = event.target.closest('[data-quiz-next]');
    if (nextButton && currentStep.contains(nextButton) && !nextButton.disabled) {
      if (currentStep.dataset.type === 'multi') {
        const selected = [...currentStep.querySelectorAll('[aria-pressed="true"]')];
        answers[keyOf(currentStep)] = {
          value: selected.map((option) => option.dataset.value || optionLabel(option)),
          selected,
        };
        trackAnswer(selected);
      }
      next();
    }
    if (event.target.closest('[data-quiz-back]')) window.history.back();
    if (event.target.closest('[data-quiz-result-link]')) track('quiz_complete');
  });

  // Restore visited screens and answers for both Back and Forward. Older page loads
  // have a different session and must not trigger extra history navigation.
  window.addEventListener('popstate', (event) => {
    if (event.state?.quizSession !== historySession) return;
    const depth = event.state.quizDepth;
    const entry = stepHistory[depth];
    if (!entry || depth === historyIndex) return;
    const direction = depth < historyIndex ? 'back' : 'forward';
    historyIndex = depth;
    answers = { ...entry.answers };
    showStep(entry.step, { remember: false, direction });
  });

  root.querySelectorAll('video').forEach((video) => {
    const toggle = video.parentElement.querySelector('[data-quiz-video-toggle]');
    const syncToggle = () => {
      toggle.toggleAttribute('data-paused', video.paused);
      toggle.setAttribute('aria-label', video.paused ? 'Play animation' : 'Pause animation');
    };
    video.addEventListener('play', syncToggle);
    video.addEventListener('pause', syncToggle);
  });

  window.history.replaceState(
    { ...window.history.state, quizSession: historySession, quizDepth: 0 },
    '',
  );
  renderProgress(currentStep);
  track('quiz_step', { quiz_direction: 'forward' });
})();
